import React, { useState, useCallback } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import css from './TalentPlusServices.module.css';
import CustomOptions from './CustomOptions';
import UnlockableContent from './UnlockableContent';
import { UNLOCKABLE_CONTENT } from '../../../../../util/constants';
import { FormattedMessage } from 'react-intl';
import '../../../../../styles/react-tooltip.css';
import { InfoPopup, GuidePopup, Icons } from '../../../../../components';
import PriceInput from './PriceInput';
import { TALENT_SERVICES_GUIDES } from '../../../../../util/enums';

const talentPlusOptions = [
  {
    service: 'Exclusive Training',
    types: [
      {
        label: '1-on-1 Coaching',
        tooltip: 'Provide personalized training and expert guidance to enhance skills.',
        guide: TALENT_SERVICES_GUIDES['1_ON_1_COACHING'],
      },
      {
        label: 'Group Training',
        tooltip: 'Lead exclusive training sessions for groups.',
        guide: TALENT_SERVICES_GUIDES.GROUP_TRAINING,
      },
      {
        label: 'Film Breakdown Session',
        tooltip: 'Offer detailed video analysis of gameplay or skills.',
        guide: TALENT_SERVICES_GUIDES.FILM_BREAKDOWN_SESSION,
      },
      {
        label: 'Virtual Training Camp',
        tooltip: 'Host online sessions designed for skill development.',
        guide: TALENT_SERVICES_GUIDES.VIRTUAL_TRAINING_CAMP,
      },
    ],
  },
  {
    service: 'Mentorship',
    types: [
      {
        label: 'Skill Development',
        tooltip: 'Offer tailored guidance to help mentees refine and improve abilities.',
        guide: TALENT_SERVICES_GUIDES.SKILL_DEVELOPMENT,
      },
      {
        label: 'Career Development Mentorship',
        tooltip: 'Provide professional guidance on career growth and opportunities.',
        guide: TALENT_SERVICES_GUIDES.CAREER_DEVELOPMENT_MENTORSHIP,
      },
      {
        label: 'Athlete Branding Consultation',
        tooltip: 'Share insights about building a personal brand in sports.',
        guide: TALENT_SERVICES_GUIDES.ATHLETE_BRANDING_CONSULTATION,
      },
      {
        label: 'Mentorship Discussion',
        tooltip: 'Help others stay on track by providing mentorship and advice.',
        guide: TALENT_SERVICES_GUIDES.MENTORSHIP_DISCUSSIONS,
      },
      {
        label: 'Mental Toughness Coaching',
        tooltip: 'Teach techniques for mindset and resilience.',
        guide: TALENT_SERVICES_GUIDES.MENTAL_TOUGHNESS_COACHING,
      },
      {
        label: 'Sponsorship Consultation',
        tooltip: 'Offer insights on securing sponsorships and brand partnerships.',
        guide: TALENT_SERVICES_GUIDES.SPONSORSHIP_CONSULTATION,
      },
    ],
  },
  {
    service: 'Business Development',
    types: [
      {
        label: 'Networking Sessions',
        tooltip: 'Lead exclusive sessions connecting people with industry professionals.',
        guide: TALENT_SERVICES_GUIDES.NETWORKING_SESSIONS,
      },
      {
        label: 'Corporate Event',
        tooltip: 'Make special appearances at corporate events.',
        guide: TALENT_SERVICES_GUIDES.CORPORATE_EVENT,
      },
      {
        label: 'Panel Discussion Participation',
        tooltip: 'Join business panels to share industry insights and experiences.',
        guide: TALENT_SERVICES_GUIDES.BUSINESS_PANEL,
      },
    ],
  },
  {
    service: 'Unlockable Content',
    types: [
      {
        label: 'Direct Message',
        tooltip:
          'Allow fans and brands to unlock the ability to message you directly for a more personal connection.',
        guide: null,
      },
      {
        label: 'Photos',
        tooltip: 'Offer fans a visual window into your world with exclusive photo content.',
        guide: null,
      },
      {
        label: 'Videos',
        tooltip:
          'Unlock dynamic video content for your fans—day-in-the-life vlogs, training tips, game recaps, bloopers, or personal stories.',
        guide: null,
      },
      {
        label: 'Posts',
        tooltip:
          'Engage fans with personal updates, journal-style entries, polls, playlists, throwback stories, or creative content drops.',
        guide: null,
      },
    ],
  },
];

const TalentPlusServices = ({
  values,
  hideTitle,
  onLiveSave,
  viewport,
  availableServices,
  hideCustomOption,
}) => {
  const [selectedService, setSelectedService] = useState({});

  const handleServiceSelect = useCallback((serviceName, isSelected) => {
    setSelectedService(prev => ({ ...prev, [serviceName]: isSelected }));
  }, []);

  // Filter talentPlusOptions based on availableServices
  const filteredOptions = availableServices
    ? talentPlusOptions.filter(option => availableServices.includes(option.service))
    : talentPlusOptions;

  return (
    <div className={css.serviceBlock}>
      {!hideTitle && (
        <div className={css.sectionHead}>
          <h2>
            <FormattedMessage id="TalentPlusServices.heading" />
          </h2>
        </div>
      )}

      <FieldArray name="talentPlus">
        {({ fields }) => (
          <div>
            {filteredOptions.map((option, index) => (
              <ServiceOption
                key={index}
                option={option}
                index={index}
                fields={fields}
                selectedService={selectedService}
                onServiceSelect={handleServiceSelect}
                values={values}
                onLiveSave={onLiveSave}
              />
            ))}
            {!hideCustomOption && (
              <CustomOptions
                fields={fields}
                talentPlusOptions={talentPlusOptions}
                viewport={viewport}
              />
            )}
          </div>
        )}
      </FieldArray>
    </div>
  );
};

const ServiceOption = ({
  option,
  index,
  fields,
  selectedService,
  onServiceSelect,
  values,
  onLiveSave,
  onManageDisableScrolling,
}) => {
  const [expandedServices, setExpandedServices] = useState([]); // Track expanded services

  const currentFields = fields.value || [];

  if (option.service === UNLOCKABLE_CONTENT) {
    return (
      <UnlockableContent
        values={values}
        option={option}
        fields={fields}
        onLiveSave={onLiveSave}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    );
  }

  const toggleServiceExpand = serviceName => {
    setExpandedServices(prev =>
      prev.includes(serviceName)
        ? prev.filter(name => name !== serviceName)
        : [...prev, serviceName]
    );
  };

  const isExpanded = expandedServices.includes(option.service);

  return (
    <div className={css.talentPlusServices}>
      <div
        role="button"
        className={css.serviceTitleRow}
        onClick={() => toggleServiceExpand(option.service)}
      >
        <h2 className={css.serviceTitle}>{option.service}</h2>
        <span className={css.iconWrapper}>
          {isExpanded ? <Icons name="upAngle" /> : <Icons name="downAngle" />}
        </span>
      </div>

      {isExpanded && (
        <div className={css.formRow}>
          {option.types.map((typeObj, typeIndex) => {
            const { label, tooltip, guide } = typeObj;

            const existingIndex = currentFields.findIndex(
              item => item.service === option.service && item.type === label
            );

            const isChecked = existingIndex !== -1;
            const initialService = isChecked ? currentFields[existingIndex] : null;

            return (
              <div key={typeIndex} className={css.formFld}>
                <label className={css.talentOptions}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={e => {
                      const isSelected = e.target.checked;

                      if (isSelected) {
                        fields.push({
                          service: option.service,
                          type: label,
                          selected: true,
                          price: initialService?.price || '',
                        });
                      } else if (existingIndex !== -1) {
                        fields.remove(existingIndex);
                      }

                      onServiceSelect(option.service, isSelected);
                    }}
                  />
                  <span className={css.typeLabel}>
                    {label}
                    <InfoPopup triggerOn="hover" info={tooltip} />
                    {guide && <GuidePopup externalLink={guide} />}
                  </span>
                </label>

                {isChecked && (
                  <PriceInput
                    type={label}
                    index={index}
                    typeIndex={typeIndex}
                    fields={fields}
                    service={option.service}
                    initialPrice={initialService?.price}
                    fieldIndex={existingIndex}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TalentPlusServices;
